import React, { useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { trackGaEvent } from '../lib/ga';

function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let mounted = true;
    let timeoutId = null;
    const params = new URLSearchParams(location.search);
    const next = params.get('next') || '/getting-started';

    function track(eventName, params = {}) {
      trackGaEvent(eventName, { page_path: '/auth/callback', ...params });
    }

    async function finalizeRouting(defaultNext) {
      try {
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        if (!session) { navigate('/login', { replace: true }); return; }
        // GA4: treat successful OAuth as sign_up (method=google)
        track('sign_up', { method: 'google' });
        const token = session.access_token;
        // Fire LinkedIn conversion once for OAuth signup/login success (guarded)
        try {
          const firedKey = 'LINKEDIN_SIGNUP_FIRED';
          if (!sessionStorage.getItem(firedKey)) {
            if (window.lintrk) window.lintrk('track', { conversion_id: 25116737 });
            sessionStorage.setItem(firedKey, '1');
          }
        } catch(_) {}
        const edgeBase = process.env.REACT_APP_SKINNY_SUPABASE_URL;
        // First-time setup: ensure tenant + app_users membership exists
        try {
          const setupResult = await supabase.functions.invoke('user-setup', { body: {} });
          if (setupResult.error) {
            console.error('[AuthCallback] user-setup returned error:', setupResult.error);
          } else {
            // eslint-disable-next-line no-console
            console.log('[AuthCallback] user-setup result:', setupResult.data);
          }
        } catch (setupErr) {
          console.error('[AuthCallback] user-setup invocation failed:', setupErr);
          // Proceed regardless — onboard-user will handle tenant creation as a fallback
        }
        // Check memberships; route to getting-started if none
        try {
          const res = await fetch(`${edgeBase}/functions/v1/user-memberships`, {
            method: 'POST',
            headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
            body: '{}'
          });
          if (res.ok) {
            const j = await res.json();
            const list = Array.isArray(j?.memberships) ? j.memberships : [];
            const needsOnboarding = list.length === 0 || list.some((m) => !m.has_onboarded);
            if (needsOnboarding) {
              navigate('/getting-started', { replace: true });
            } else {
              navigate(defaultNext, { replace: true });
            }
            return;
          }
        } catch (_) { /* ignore */ }
        navigate(defaultNext, { replace: true });
      } catch (_) {
        navigate('/login', { replace: true });
      }
    }

    // Subscribe to auth state to catch session hydration from hash, then run setup + route
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (!mounted) return;
      if (event === 'SIGNED_IN') {
        finalizeRouting(next);
      }
    });

    (async () => {
      try {
        // Initial check
        const { data } = await supabase.auth.getSession();
        const session = data?.session;
        if (!mounted) return;
        if (session) {
          finalizeRouting(next);
        } else {
          // Give detectSessionInUrl a brief moment to process the hash
          timeoutId = setTimeout(async () => {
            if (!mounted) return;
            const { data: d2 } = await supabase.auth.getSession();
            if (d2?.session) finalizeRouting(next);
            else navigate('/login', { replace: true });
          }, 1500);
        }
      } catch (_) {
        if (mounted) navigate('/login', { replace: true });
      }
    })();

    return () => {
      mounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      try { sub.subscription.unsubscribe(); } catch (_) {}
    };
  }, [navigate, location.search]);

  return (
    <Container className="py-5 text-center">
      <Spinner animation="border" size="sm" className="me-2" />
      Signing you in...
    </Container>
  );
}

export default AuthCallback;


